import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext } from "react-router";
import { FormInput } from "../../components/FormInput";
import NoInfo from "../../components/NoInfo";
import PrimeTableFooter from "../../components/PrimeTableFooter";
import PrimeDialog from "../../components/dialog/PrimeDialog";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import { createNewRole, getRoleList, updateRole, F2020109Before } from "../../utility/service/personal-service";
import { checkDpSuccess, getCompanyType } from "../../utility/service/tool-service";
import AddRole from "./AddRole";
import UpdateRole from "./UpdateRole";
import { TreeTable } from "primereact/treetable";
import { AcctContext } from "../../utility/useReduce/userNameReduce";

const AccessControl_Index = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [state, dispatchUser] = useContext(AcctContext); //訊息視窗
  const { setItemObj, setAction, currentStatus, setCurrentStatus } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [roleSeq, setRoleSeq] = useState(""); //分頁用key
  const [confirmLoad, setConfirmLoad] = useState(false);
  const isEditable = useEditableFlag("F202"); // Get editable status for F202 function
  const [modifyRoleDialog, setModifyRoleDialog] = useState(false);
  const [, loading] = useContext(SystemLoading);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentRoleKey, setCurrentRoleKey] = useState(null);
  const [expandedNodeKeys, setExpandedNodeKeys] = useState({}); // 展開狀態
  const [treeOptions, setTreeOptions] = useState([]);
  // 簽核標記
  const approveFlag = state?.approveFlag;

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setValue,
    resetField,
    reset: resetForm,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {
      role: "",
    },
    //錯誤驗證時機
    mode: "all",
  });

  const btnFunList = (rowData) => {
    setItemObj(rowData);
    navigate("funlist", { state: { dataList: rows } });
    setCurrentStatus({
      roleSeq: getValues("roleSeq") === undefined ? "" : getValues("roleSeq"),
    });
  };

  const btnGroupSetting = (rowData) => {
    setItemObj({ ...rowData?.data, last: rowData?.last });
    navigate("group");
    setCurrentStatus({
      roleSeq: getValues("roleSeq") === undefined ? "" : getValues("roleSeq"),
    });
  };

  const btnPlaceSetting = (rowData) => {
    setItemObj(rowData);
    navigate("place");
    setCurrentStatus({
      roleSeq: getValues("roleSeq") === undefined ? "" : getValues("roleSeq"),
    });
  };

  const btnProductSetting = (rowData) => {
    setItemObj(rowData);
    navigate("product");
    setCurrentStatus({
      roleSeq: getValues("roleSeq") === undefined ? "" : getValues("roleSeq"),
    });
  };

  useEffect(() => {
    axios_getRoleList({}, "initData");
  }, []);

  // 新增角色
  const confirmAddRole = () => {
    setConfirmLoad(true);
    let reqBody = {
      symbol: getValues("symbol"),
      name: getValues("name"),
      ...(approveFlag && { parentId: getValues("parentId") }),
    };
    checkLoading(loading);
    createNewRole(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          setConfirmLoad(false);
          setAddDialog(false);
          setValue("parentId", "");
          setValue("symbol", "");
          setValue("name", "");
          handleSuccessMessage(dispatch, t("button.create") + " " + t("message.success"));
          axios_getRoleList();
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  // 取得角色
  const axios_getRoleList = (reqBody = {}, initRoleSeq = "", readMore) => {
    getRoleList(reqBody).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.roleList;
        if (data?.length > 0) {
          setRoleSeq(data[data?.length - 1].roleSeq);
          setExpandedNodeKeys(buildExpandedKeys(data));
        }
        if (readMore === true) {
          setRows((pre) => {
            return [...pre, ...data];
          });
        } else {
          setRows(data);
        }

        if (initRoleSeq === "initData") {
          setRows(data);
        }
      }
    });
  };

  useEffect(() => {
    if (rows?.length) {
      setTreeOptions(formatToTreeSelect(rows));
    }
  }, [rows]);

  // 父節點下拉選項
  const formatToTreeSelect = (nodes) => {
    return nodes.map((node) => ({
      key: node.key,
      label: node.data.name,
      data: node.data,
      children: node.children ? formatToTreeSelect(node.children) : null,
    }));
  };

  // 新增角色父節點下拉：前端排除 admin
  const filterAdminFromTree = (nodes = []) => {
    return nodes
      .filter((node) => {
        const isAdminNode = String(node?.data?.symbol || "")
          .trim()
          .toLowerCase() === "admin";
        return !isAdminNode;
      })
      .map((node) => ({
        ...node,
        children: node.children ? filterAdminFromTree(node.children) : null,
      }));
  };

  const addDialogTreeOptions = useMemo(() => filterAdminFromTree(treeOptions), [treeOptions]);

  // 全部展開角色
  const buildExpandedKeys = (nodes) => {
    const keys = {};

    const walk = (list) => {
      list?.forEach((node) => {
        if (node.key != null) {
          keys[node.key] = true;
        }

        if (node.children?.length) {
          walk(node.children);
        }
      });
    };

    walk(nodes);
    return keys;
  };

  // 角色更新
  const handleConfirmModifyRole = () => {
    checkLoading(loading, true);
    const reqBody = {
      roleSeq: selectedRow?.roleSeq,
      symbol: getValues("updateSymbol"),
      name: getValues("updateName"),
      ...(approveFlag && { parentId: getValues("updateParentId") }),
    };

    updateRole(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          axios_getRoleList();
          setModifyRoleDialog(false);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      const isAdmin = rowData?.data?.symbol === "ADMIN";
      return (
        <>
          <Button
            tooltip={t("button.edit")}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            disabled={isAdmin}
            onClick={() => {
              clearErrors();
              setModifyRoleDialog(true);
              setSelectedRow(rowData?.data);
              setCurrentRoleKey(rowData?.key);
              setValue("updateParentId", rowData?.data?.parentId);
              setValue("updateSymbol", rowData?.data?.symbol);
              setValue("updateName", rowData?.data?.name);
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-pen" />
          </Button>
          <Button
            tooltip={t("role_fun_list")}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              btnFunList(rowData?.data);
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-list-check" />
          </Button>

          <Button
            tooltip={t("role_group")}
            tooltipOptions={{ position: "top" }}
            rounded
            text
            severity="secondary"
            className="p-button-icon-only"
            onClick={() => {
              btnGroupSetting(rowData);
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-user-group" />
          </Button>

          {getCompanyType() === "A" && (
            <Button
              disabled={!isEditable}
              tooltip={t("role_place")}
              tooltipOptions={{ position: "top" }}
              rounded
              text
              severity="secondary"
              className="p-button-icon-only"
              onClick={() => {
                btnPlaceSetting(rowData?.data);
              }}
            >
              <FontAwesomeIcon icon="fa-solid fa-location-dot" />
            </Button>
          )}

          {getCompanyType() === "A" && (
            <Button
              disabled={!isEditable}
              tooltip={t("role_product")}
              tooltipOptions={{ position: "top" }}
              rounded
              text
              severity="secondary"
              className="p-button-icon-only"
              onClick={() => {
                btnProductSetting(rowData?.data);
              }}
            >
              <FontAwesomeIcon icon="fa-solid fa-box" />
            </Button>
          )}
        </>
      );
    }
  };

  const btnReadMore = () => {
    let reqBody = {
      roleSeq: roleSeq,
    };
    axios_getRoleList(reqBody, "", true);
  };

  return (
    <>
      <div className="p-card">
        <div className="p-card-body">
          <Button
            className="mb-3"
            outlined
            disabled={!isEditable}
            onClick={() => {
              setAddDialog(true);
              clearErrors();
            }}
            type="button"
            label={t("button.add")}
          />

          <TreeTable
            className="white-data-table hover-highlight no-shadow"
            expandedKeys={expandedNodeKeys}
            onToggle={(e) => setExpandedNodeKeys(e.value)}
            tableStyle={{ minWidth: "55rem" }}
            footer={
              rows?.length > 0 && <PrimeTableFooter rowDataList={rows} isShowMore={true} btnReadMore={btnReadMore} />
            }
            value={rows}
            emptyMessage={<NoInfo panel="table" />}
          >
            {/* 改用流水號 不用roleSeq */}
            <Column field="symbol" header={t("role_symbol")} expander />
            <Column field="name" header={t("role_name")} />
            <Column style={{ width: "auto" }} body={actionBodyTemplate} header={t("action")} />
          </TreeTable>
        </div>
      </div>

      {/*角色編輯*/}
      <PrimeDialog
        size="sm"
        visible={modifyRoleDialog}
        onClose={() => {
          setModifyRoleDialog(false);
          setValue("parentId", "");
          setValue("symbol", "");
          setValue("name", "");
        }}
        disabledConfirm={!isValid}
        onConfirm={() => handleConfirmModifyRole()}
        title={t("button.edit")}
        bodyTemplate={
          <UpdateRole
            register={register}
            errors={errors}
            control={control}
            treeOptions={treeOptions}
            currentRoleKey={currentRoleKey}
            watch={watch}
            setValue={setValue}
            approveFlag={approveFlag}
          />
        }
      />

      {/* 新增 Dialog */}
      <PrimeDialog
        visible={addDialog}
        onClose={() => {
          setAddDialog(false);
          setValue("symbol", "");
          setValue("name", "");
        }}
        disabledConfirm={!isValid}
        onConfirm={() => {
          confirmAddRole();
        }}
        title={t("button.add")}
        size="sm"
        bodyTemplate={
          <AddRole
            register={register}
            errors={errors}
            control={control}
            treeOptions={addDialogTreeOptions}
            setValue={setValue}
            approveFlag={approveFlag}
          />
        }
      />
    </>
  );
};

export default AccessControl_Index;
